import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Navigate, Link as RouterLink } from "react-router-dom";

import ClientAuthLayout from "../components/ClientAuthLayout";
import { useDataStore } from "../dataStore";
import { useClientPortal } from "../clientPortal/ClientPortalContext";
import { CLIENT_PORTAL_PATHS } from "../clientPortal/clientPortalConstants";
import {
  buildClientRegistrationPayload,
  normalizeRegistrationCounties,
  normalizeRegistrationCities,
  validateClientRegistrationForm,
} from "../clientPortal/clientPortalRegistration";
import { DEFAULT_BRAND_THEME } from "../themeTokens";

const EMPTY_FORM = Object.freeze({
  firstName: "",
  lastName: "",
  email: "",
  county: null,
  city: null,
  password: "",
  passwordConfirmation: "",
});

const ACCOUNT_EXISTS_CODES = new Set([
  "CLNT-160",
  "CLNT-170",
  "CLNT-190",
  "CLNT-200",
]);

const FIELD_IDS = Object.freeze({
  firstName: "client-first-name",
  lastName: "client-last-name",
  email: "client-register-email",
  county: "client-county",
  city: "client-city",
  password: "client-register-password",
  passwordConfirmation: "client-register-password-confirmation",
});

export default function ClientRegisterPage() {
  const {
    brandTheme,
    languageReady,
    selectedLanguageIref,
    t,
  } = useDataStore();
  const c = { ...DEFAULT_BRAND_THEME, ...brandTheme };
  const {
    enabled,
    status,
    isAuthenticated,
    loadRegistrationCounties,
    loadRegistrationCities,
    registerClientAccount,
  } = useClientPortal();
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [counties, setCounties] = useState([]);
  const [countiesLoading, setCountiesLoading] = useState(true);
  const [countiesError, setCountiesError] = useState(false);
  const [countyLoadVersion, setCountyLoadVersion] = useState(0);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesError, setCitiesError] = useState(false);
  const [cityLoadVersion, setCityLoadVersion] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [complete, setComplete] = useState(false);
  const submittingRef = useRef(false);

  const restoring = status === "restoring";
  const updateField = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setSubmitError("");
  }, []);

  useEffect(() => {
    if (!enabled || isAuthenticated) return undefined;
    let active = true;
    setCountiesLoading(true);
    setCountiesError(false);

    loadRegistrationCounties()
      .then((raw) => {
        if (!active) return;
        setCounties(normalizeRegistrationCounties(raw));
        setCountiesLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setCounties([]);
        setCountiesLoading(false);
        setCountiesError(true);
      });

    return () => {
      active = false;
    };
  }, [
    countyLoadVersion,
    enabled,
    isAuthenticated,
    loadRegistrationCounties,
  ]);

  useEffect(() => {
    const countyId = form.county?.id;
    if (!enabled || isAuthenticated || !countyId) {
      setCities([]);
      setCitiesLoading(false);
      setCitiesError(false);
      return undefined;
    }

    let active = true;
    setCities([]);
    setCitiesLoading(true);
    setCitiesError(false);

    loadRegistrationCities(countyId)
      .then((raw) => {
        if (!active) return;
        setCities(normalizeRegistrationCities(raw));
        setCitiesLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setCities([]);
        setCitiesLoading(false);
        setCitiesError(true);
      });

    return () => {
      active = false;
    };
  }, [
    cityLoadVersion,
    enabled,
    form.county?.id,
    isAuthenticated,
    loadRegistrationCities,
  ]);

  const updateCounty = useCallback((value) => {
    setForm((current) => ({
      ...current,
      county: value,
      city: null,
    }));
    setFieldErrors((current) => {
      if (!current.county && !current.city) return current;
      const next = { ...current };
      delete next.county;
      delete next.city;
      return next;
    });
    setCities([]);
    setCitiesError(false);
    setSubmitError("");
  }, []);

  if (!enabled) return <Navigate to="/" replace />;
  if (isAuthenticated) {
    return <Navigate to={CLIENT_PORTAL_PATHS.account} replace />;
  }

  const errorText = (error) => {
    const key = {
      required: "portal.register.required",
      email: "portal.register.emailInvalid",
      password: "portal.register.passwordInvalid",
      match: "portal.register.passwordMismatch",
    }[error];
    return key ? t(key) : "";
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || restoring) return;

    const nextErrors = validateClientRegistrationForm(form);
    setFieldErrors(nextErrors);
    setSubmitError("");
    const firstInvalidField = Object.keys(nextErrors)[0];
    if (firstInvalidField) {
      document.getElementById(FIELD_IDS[firstInvalidField])?.focus();
      return;
    }
    if (!languageReady || !selectedLanguageIref) {
      setSubmitError("language");
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const payload = buildClientRegistrationPayload(
        form,
        selectedLanguageIref
      );
      await registerClientAccount(payload);
      setForm(EMPTY_FORM);
      setComplete(true);
    } catch (error) {
      setSubmitError(
        ACCOUNT_EXISTS_CODES.has(String(error?.code || ""))
          ? "exists"
          : "failed"
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const aside = (
    <Stack spacing={2.25}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "16px",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.18)",
        }}
      >
        <PersonAddAlt1RoundedIcon
          sx={{ fontSize: 28, color: "#fff" }}
          aria-hidden="true"
        />
      </Box>
      <Box>
        <Typography component="h2" variant="h5" fontWeight={950}>
          {t("portal.register.haveAccount")}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
          {t("portal.register.haveAccountText")}
        </Typography>
      </Box>
      <Button
        component={RouterLink}
        to={CLIENT_PORTAL_PATHS.login}
        variant="outlined"
        sx={{
          alignSelf: "flex-start",
          minHeight: 46,
          px: 2.25,
          borderRadius: "14px",
        }}
      >
        {t("portal.register.signIn")}
      </Button>
    </Stack>
  );

  return (
    <ClientAuthLayout aside={aside}>
      {complete ? (
        <Stack spacing={2.5} alignItems="center" textAlign="center" sx={{ py: 2 }}>
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: "24px",
              display: "grid",
              placeItems: "center",
              color: "#0f766e",
              bgcolor: "rgba(15,118,110,0.10)",
              border: "1px solid rgba(15,118,110,0.16)",
              boxShadow: "0 18px 38px rgba(15,118,110,0.12)",
            }}
          >
            <CheckCircleRoundedIcon
              sx={{ fontSize: 44 }}
              aria-hidden="true"
            />
          </Box>
          <Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{ color: c.text, fontSize: { xs: 30, sm: 38 } }}
            >
              {t("portal.register.successTitle")}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
              {t("portal.register.successText")}
            </Typography>
          </Box>
          <Alert
            severity="info"
            sx={{
              textAlign: "left",
              width: "100%",
              borderRadius: "14px",
            }}
          >
            {t("portal.register.validationInfo")}
          </Alert>
          <Button
            component={RouterLink}
            to={CLIENT_PORTAL_PATHS.login}
            variant="contained"
            sx={{
              minHeight: 50,
              px: 4,
              borderRadius: "15px",
              bgcolor: c.primary,
              color: c.onPrimary,
              boxShadow: "0 14px 30px rgba(247,181,0,0.22)",
              "&:hover": { bgcolor: c.primary },
            }}
          >
            {t("portal.register.signIn")}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={{ xs: 2.25, sm: 2.75 }}>
          <Stack
            spacing={1.1}
            alignItems={{ xs: "center", sm: "flex-start" }}
            textAlign={{ xs: "center", sm: "left" }}
          >
            <Chip
              icon={<ShieldOutlinedIcon />}
              label={t("portal.account.title")}
              size="small"
              sx={{
                color: c.text,
                bgcolor: "rgba(247,181,0,0.14)",
                border: "1px solid rgba(124,45,18,0.10)",
                "& .MuiChip-icon": { color: c.accent },
              }}
            />
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: "19px",
                display: "grid",
                placeItems: "center",
                bgcolor: c.primary,
                color: c.onPrimary,
                boxShadow: "0 16px 34px rgba(247,181,0,0.22)",
              }}
            >
              <PersonAddAlt1RoundedIcon />
            </Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                color: c.text,
                fontSize: { xs: 30, sm: 38 },
                lineHeight: 1.08,
              }}
            >
              {t("portal.register.title")}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ maxWidth: 560, lineHeight: 1.6 }}
            >
              {t("portal.register.subtitle")}
            </Typography>
          </Stack>

          {countiesError ? (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setCountyLoadVersion((value) => value + 1)}
                >
                  {t("portal.common.retry")}
                </Button>
              }
            >
              {t("portal.register.countiesError")}
            </Alert>
          ) : null}

          {citiesError ? (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setCityLoadVersion((value) => value + 1)}
                >
                  {t("portal.common.retry")}
                </Button>
              }
            >
              {t("portal.register.citiesError")}
            </Alert>
          ) : null}

          {submitError ? (
            <Alert id="client-register-error" severity="error" role="alert">
              {t(
                submitError === "exists"
                  ? "portal.register.exists"
                  : submitError === "language"
                    ? "portal.register.languageUnavailable"
                    : "portal.register.failed"
              )}
            </Alert>
          ) : null}

          <Box
            component="form"
            onSubmit={onSubmit}
            aria-busy={submitting || restoring}
            noValidate
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: "18px",
              bgcolor: "rgba(17,17,17,0.025)",
              border: "1px solid rgba(17,17,17,0.06)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: { xs: 1.5, sm: 1.75 },
                alignItems: "flex-start",
              }}
            >
              <TextField
                id="client-first-name"
                label={t("portal.register.firstName")}
                value={form.firstName}
                onChange={(event) =>
                  updateField("firstName", event.target.value)
                }
                autoComplete="given-name"
                inputProps={{ maxLength: 128 }}
                required
                fullWidth
                disabled={submitting || restoring}
                error={Boolean(fieldErrors.firstName)}
                helperText={errorText(fieldErrors.firstName)}
              />
              <TextField
                id="client-last-name"
                label={t("portal.register.lastName")}
                value={form.lastName}
                onChange={(event) =>
                  updateField("lastName", event.target.value)
                }
                autoComplete="family-name"
                inputProps={{ maxLength: 128 }}
                required
                fullWidth
                disabled={submitting || restoring}
                error={Boolean(fieldErrors.lastName)}
                helperText={errorText(fieldErrors.lastName)}
              />

              <TextField
                id="client-register-email"
                label={t("portal.register.email")}
                type="email"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                autoComplete="email"
                inputProps={{ maxLength: 128 }}
                required
                fullWidth
                disabled={submitting || restoring}
                error={Boolean(fieldErrors.email)}
                helperText={errorText(fieldErrors.email)}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              />

              <Autocomplete
                id="client-county"
                options={counties}
                value={form.county}
                onChange={(_event, value) => updateCounty(value)}
                onInputChange={(_event, value, reason) => {
                  if (
                    reason === "input" &&
                    form.county &&
                    value !== form.county.label
                  ) {
                    updateCounty(null);
                  }
                }}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id
                }
                loading={countiesLoading}
                disabled={submitting || restoring || countiesError}
                loadingText={t("portal.register.countiesLoading")}
                noOptionsText={t("portal.register.noCounties")}
                fullWidth
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("portal.register.county")}
                    required
                    error={Boolean(fieldErrors.county)}
                    helperText={errorText(fieldErrors.county)}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "address-level1",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {countiesLoading ? (
                            <CircularProgress
                              color="inherit"
                              size={18}
                              aria-hidden="true"
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                id="client-city"
                options={cities}
                value={form.city}
                onChange={(_event, value) => updateField("city", value)}
                onInputChange={(_event, value, reason) => {
                  if (
                    reason === "input" &&
                    form.city &&
                    value !== form.city.label
                  ) {
                    updateField("city", null);
                  }
                }}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id
                }
                loading={citiesLoading}
                disabled={
                  submitting ||
                  restoring ||
                  countiesLoading ||
                  countiesError ||
                  !form.county ||
                  citiesError
                }
                loadingText={t("portal.register.citiesLoading")}
                noOptionsText={t("portal.register.noCities")}
                fullWidth
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("portal.register.city")}
                    required
                    error={Boolean(fieldErrors.city)}
                    helperText={errorText(fieldErrors.city)}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "address-level2",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {citiesLoading ? (
                            <CircularProgress
                              color="inherit"
                              size={18}
                              aria-hidden="true"
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <TextField
                id="client-register-password"
                label={t("portal.register.password")}
                type="password"
                value={form.password}
                onChange={(event) =>
                  updateField("password", event.target.value)
                }
                autoComplete="new-password"
                inputProps={{ maxLength: 32 }}
                required
                fullWidth
                disabled={submitting || restoring}
                error={Boolean(fieldErrors.password)}
                helperText={
                  errorText(fieldErrors.password) ||
                  t("portal.register.passwordHint")
                }
              />
              <TextField
                id="client-register-password-confirmation"
                label={t("portal.register.passwordConfirmation")}
                type="password"
                value={form.passwordConfirmation}
                onChange={(event) =>
                  updateField(
                    "passwordConfirmation",
                    event.target.value
                  )
                }
                autoComplete="new-password"
                inputProps={{ maxLength: 32 }}
                required
                fullWidth
                disabled={submitting || restoring}
                error={Boolean(fieldErrors.passwordConfirmation)}
                helperText={errorText(
                  fieldErrors.passwordConfirmation
                )}
              />

              <Button
                type="submit"
                variant="contained"
                disabled={
                  submitting ||
                  restoring ||
                  countiesLoading ||
                  countiesError ||
                  citiesLoading ||
                  citiesError
                }
                sx={{
                  minHeight: 52,
                  mt: 0.5,
                  borderRadius: "15px",
                  gridColumn: { sm: "1 / -1" },
                  bgcolor: c.primary,
                  color: c.onPrimary,
                  boxShadow: "0 14px 30px rgba(247,181,0,0.22)",
                  "&:hover": {
                    bgcolor: c.primary,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {submitting ? (
                  <>
                    <CircularProgress
                      size={18}
                      color="inherit"
                      sx={{ mr: 1 }}
                      aria-hidden="true"
                    />
                    {t("portal.register.pending")}
                  </>
                ) : restoring ? (
                  t("portal.login.restoring")
                ) : (
                  t("portal.register.submit")
                )}
              </Button>
            </Box>
          </Box>
        </Stack>
      )}
    </ClientAuthLayout>
  );
}
